import React, { useState, useEffect } from 'react';
import useUserInfo from '../../../hooks/useUserInfo';
import { useRestaurant } from '../../../context/RestaurantContext';
import useSwitchRestaurant from '../../../hooks/useSwitchRestaurant';
import useSignedImage from '../../../hooks/useSignedImage';
import './restaurantSelector.css';

const RestaurantOption = ({ restaurant, isActive, onSelect }) => {

  const { imageUrl: restaurantImageUrl } = useSignedImage(
    restaurant.restaurantImage, 
    '/default-restaurant.png'
  );

  return (
    <div
      className={`restaurant-selector__option ${isActive ? 'active' : ''}`}
      onClick={onSelect}
    >
      <img 
        src={restaurantImageUrl} 
        alt={restaurant.restaurantName}
        className="restaurant-selector__option-image"
        onError={(e) => {
          e.target.src = '/default-restaurant.png';
        }}
      />
      <div className="restaurant-selector__option-details">
        <span className="restaurant-selector__option-name">
          {restaurant.restaurantName}
        </span>
        <span className="restaurant-selector__option-role">
          {restaurant.role}
        </span>
      </div>
    </div>
  );
};

const RestaurantSelector = () => {
  const { userInfo, refreshUserInfo } = useUserInfo();
  const { restaurantId } = useRestaurant();
  const { switchRestaurant: switchRestaurantAPI } = useSwitchRestaurant();
  const [isOpen, setIsOpen] = useState(false);
  const currentRestaurantId = restaurantId || userInfo?.restaurants?.[0]?.restaurantId;
  const currentRestaurant = userInfo?.restaurants?.find(r => r.restaurantId === currentRestaurantId);
  const { imageUrl: currentRestaurantImageUrl } = useSignedImage(
    currentRestaurant?.restaurantImage, 
    '/default-restaurant.png'
  );

  useEffect(() => {
    const handleRestaurantUpdate = () => {
      refreshUserInfo();
    };

    window.addEventListener('restaurantUpdated', handleRestaurantUpdate);
    
    return () => {
      window.removeEventListener('restaurantUpdated', handleRestaurantUpdate);
    };
  }, [refreshUserInfo]);

  // Don't show selector if user has only one or no restaurants
  if (!userInfo || !userInfo.restaurants || userInfo.restaurants.length <= 1) {
    return null;
  }

  const handleRestaurantSwitch = async (newRestaurantId) => {
    await switchRestaurantAPI(newRestaurantId);
    setIsOpen(false);
  };

  return (
    <div className="restaurant-selector">
      <div 
        className="restaurant-selector__current"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="restaurant-selector__current-info">
          <img 
            src={currentRestaurantImageUrl} 
            alt={currentRestaurant?.restaurantName || 'Restaurant'}
            className="restaurant-selector__current-image"
            onError={(e) => {
              e.target.src = '/default-restaurant.png';
            }}
          />
          <div className="restaurant-selector__current-details">
            <span className="restaurant-selector__current-name">
              {currentRestaurant?.restaurantName || 'Restaurant'}
            </span>
            <span className="restaurant-selector__current-role">
              {currentRestaurant?.role || 'Staff'}
            </span>
          </div>
        </div>
        <div className={`restaurant-selector__arrow ${isOpen ? 'open' : ''}`}>
          ▼
        </div>
      </div>

      {isOpen && (
        <div className="restaurant-selector__dropdown">
          {userInfo.restaurants.map((restaurant) => (
            <RestaurantOption
              key={restaurant.restaurantId}
              restaurant={restaurant}
              isActive={restaurant.restaurantId === currentRestaurantId}
              onSelect={() => handleRestaurantSwitch(restaurant.restaurantId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RestaurantSelector;
